import { useState } from 'react'
import { useLoadingGate } from '@/lib/useLoadingGate'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'

/** The mihFIBER wordmark, cut from logo.png with a transparent background (698×117). */
const WORDMARK = '/loader-wordmark.png'
const ASPECT = 698 / 117

/**
 * A loader that fills the mihFIBER wordmark as something loads.
 *
 * The letters start as a faint outline and fill left to right — "mih" in its silver, "FIBER" in
 * its green — with a pulse of light riding the leading edge, the way light runs down a fibre.
 * The light is masked to the letterforms, so it only ever travels through the text. Below, a
 * hairline track echoes the rules around the logo's tagline.
 *
 * `progress` is 0..1. Under reduced motion the pulse is dropped; the fill still tracks progress.
 */
export function LogoLoader({ progress, label: labelProp }: { progress: number; label?: string }) {
  const tc = useT(commonMessages)
  const label = labelProp ?? tc('loading').replace('…', '')
  const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100)
  const mask = {
    WebkitMaskImage: `url(${WORDMARK})`,
    maskImage: `url(${WORDMARK})`,
    WebkitMaskSize: '100% 100%',
    maskSize: '100% 100%',
  } as const

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      // A dark rounded tile, after the app icon's own black square, so the wordmark reads the
      // same over a bright photo, a dark one, or a map.
      className="frost-solid-dark flex flex-col items-center rounded-[28px] bg-neutral-950/85 px-7 pb-5 pt-7 shadow-2xl ring-1 ring-white/10 backdrop-blur-xl"
    >
      <div className="relative w-[min(60vw,280px)]" style={{ aspectRatio: ASPECT }}>
        {/* The empty letters. */}
        <img src={WORDMARK} alt="" draggable={false} className="absolute inset-0 h-full w-full select-none opacity-[0.2] grayscale" />
        {/* The filled letters, revealed up to the progress line. */}
        <img
          src={WORDMARK}
          alt=""
          draggable={false}
          className="absolute inset-0 h-full w-full select-none transition-[clip-path] duration-300 ease-out"
          style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
        />
        {/* Light through the fibre: a bright band at the fill edge, visible only inside the
            letters, plus a slow shimmer across what has already filled. */}
        <div className="pointer-events-none absolute inset-0" style={mask}>
          <div
            className="absolute inset-y-0 w-[18%] -translate-x-1/2 transition-[left] duration-300 ease-out motion-reduce:hidden"
            style={{
              left: `${pct}%`,
              background: 'linear-gradient(90deg, transparent, rgb(255 255 255 / 0.95) 50%, transparent)',
              mixBlendMode: 'screen',
            }}
          />
          <div
            className="logo-shimmer absolute inset-y-0 left-0 motion-reduce:hidden"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Hairline track with a glowing head — the logo's tagline rules, put to work. */}
      <div className="relative mt-4 h-px w-[min(60vw,280px)] bg-white/15">
        <div className="absolute inset-y-0 left-0 bg-[#7cc420] transition-[width] duration-300 ease-out" style={{ width: `${pct}%` }} />
        <div
          className="absolute top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#b9f36a] shadow-[0_0_10px_3px_rgb(124_196_32/0.7)] transition-[left] duration-300 ease-out"
          style={{ left: `${pct}%` }}
        />
      </div>
      <div className="tabular mt-2.5 text-[11px] font-medium uppercase tracking-[0.25em] text-white/60">
        {label} · {pct}%
      </div>
    </div>
  )
}

/**
 * The compact, non-blocking form: a small pill with the wordmark filling, for loads that should
 * not cover what is already usable (the RTK map stays pannable while its routes stream in).
 * With `onRetry` it becomes the failure state instead of a progress indicator.
 */
export function LogoLoaderPill({ progress, label, onRetry }: { progress: number; label: string; onRetry?: () => void }) {
  const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100)
  const failed = !!onRetry
  const tc = useT(commonMessages)
  return (
    <div
      role={failed ? 'alert' : 'progressbar'}
      aria-label={label}
      {...(failed ? {} : { 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': pct })}
      className="frost-solid-dark overlay-fade pointer-events-auto flex items-center gap-3 rounded-full bg-neutral-950/85 py-2 pl-4 pr-2 text-white shadow-xl ring-1 ring-white/10 backdrop-blur-xl"
    >
      <div className="relative h-4 w-24 shrink-0" style={{ aspectRatio: ASPECT }}>
        <img src={WORDMARK} alt="" draggable={false} className="absolute inset-0 h-full w-full select-none opacity-[0.2] grayscale" />
        {!failed && (
          <img
            src={WORDMARK}
            alt=""
            draggable={false}
            className="absolute inset-0 h-full w-full select-none transition-[clip-path] duration-300 ease-out"
            style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
          />
        )}
      </div>
      <span className="tabular whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.18em] text-white/70">
        {failed ? label : `${label} · ${pct}%`}
      </span>
      {failed ? (
        <button
          type="button"
          onClick={onRetry}
          className="h-8 rounded-full bg-brand-600 px-3 text-xs font-semibold text-white hover:bg-brand-500"
        >
          {tc('retry')}
        </button>
      ) : <span className="w-1" />}
    </div>
  )
}

/**
 * A page's loading state, as a hook so it fits the early-return pattern the pages already use:
 *
 *     const loader = usePageLoader(isPending, 'Loading dashboard')
 *     if (loader) return loader
 *
 * Returns nothing for a fast load. Otherwise the wordmark fills while `loading`, completes when
 * it ends, and only then hands the page back — which is why it has to live in the page itself:
 * an element returned only while loading would be unmounted before it could finish.
 */
export function usePageLoader(loading: boolean, label: string): React.ReactElement | null {
  const { show, progress } = useLoadingGate(loading)
  if (show) {
    return (
      <div className="overlay-fade grid min-h-[60dvh] place-items-center">
        <LogoLoader progress={progress} label={label} />
      </div>
    )
  }
  // Inside the 160 ms grace period: hold an empty page rather than a half-rendered one.
  if (loading) return <div className="min-h-[60dvh]" aria-busy="true" />
  return null
}

/** When the current lazy route began loading, so its page can continue the same fill. */
let routeLoadStartedAt: number | null = null

/**
 * Suspense fallback for a lazily loaded route (RTK's MapLibre chunk). Records when it started so
 * the page's own data loader — `useRouteLoadStart()` — carries the fill on from here.
 */
export function RouteLoader({ label }: { label: string }) {
  const [startedAt] = useState(() => (routeLoadStartedAt = performance.now()))
  const { show, progress } = useLoadingGate(true, startedAt)
  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-neutral-100 dark:bg-neutral-950">
      {show && <LogoLoader progress={progress} label={label} />}
    </div>
  )
}

/**
 * The start of the route load in progress, if it began moments ago.
 *
 * Read-only on purpose: React may call a state initializer twice (Strict Mode does, in
 * development), and clearing the value here made the second call miss it and restart the fill at
 * 0%. The page calls `routeLoadFinished()` once its data is in.
 */
export function useRouteLoadStart(): number | undefined {
  const [at] = useState(() => {
    const t = routeLoadStartedAt
    return t != null && performance.now() - t < 15_000 ? t : undefined
  })
  return at
}

/** Ends the route load, so a later visit (or a later reload of the data) starts its own fill. */
export function routeLoadFinished() {
  routeLoadStartedAt = null
}
